import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ShieldCheck, Truck, CreditCard, Headphones, ShoppingBag, Store, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { ProductCard } from "@/components/ProductCard";
import { LocationPopup } from "@/components/LocationPopup";
import { STORE } from "@/lib/store";

const HERO = "https://images.unsplash.com/photo-1556911220-bff31c812dba?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const SLIDES = ["/slides/slide1.png", "/slides/slide2.png"];

const CATS = [
  { key: "refrigerators", label: "cat.refrigerators", to: "/boutique?category=refrigerators", q: { category: "refrigerators" }, fallback: "https://images.unsplash.com/photo-1571175443880-49e1d25b2bc5?w=800" },
  { key: "ranges", label: "cat.ranges", to: "/boutique?category=ranges", q: { category: "ranges" }, fallback: "https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=800" },
  { key: "washers", label: "cat.washers", to: "/boutique?category=washers", q: { category: "washers" }, fallback: "https://images.unsplash.com/photo-1626806787461-102c1bfaaea1?w=800" },
  { key: "dishwashers", label: "cat.dishwashers", to: "/boutique?category=dishwashers", q: { category: "dishwashers" }, fallback: "https://images.unsplash.com/photo-1585351923007-bf6a01cb19de?w=800" },
  { key: "range-hoods", label: "cat.range-hoods", to: "/boutique?category=range-hoods", q: { category: "range-hoods" }, fallback: "https://ca.vesta.kitchen/cdn/shop/files/VRH-BOSTON_01_Front_Low.png?v=1752246700&width=800" },
  { key: "freezers", label: "cat.freezers", to: "/boutique?category=freezers", q: { category: "freezers" }, fallback: "https://images.unsplash.com/photo-1610557892470-55d9e80c0bce?w=800" },
  { key: "mattresses", label: "nav.mattresses", to: "/boutique?category=mattresses", q: { category: "mattresses" }, fallback: "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?w=800" },
  { key: "if-beds", label: "cat.beds", to: "/boutique?category=if-beds", q: { category: "if-beds" }, fallback: "https://images.unsplash.com/photo-1505693314120-0d443867891c?w=800" },
];

export default function Home() {
  const { t } = useI18n();
  const [featured, setFeatured] = useState([]);
  const [catImgs, setCatImgs] = useState({});
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    api.get("/products", { params: { category: "if-beds", sort: "price_asc", min_price: 1, limit: 16 } })
      .then((r) => setFeatured((r.data.products || []).filter((p) => p.image && !p.price_on_request).slice(0, 8)))
      .catch(() => {});
    CATS.forEach((c) => {
      api.get("/products", { params: { ...c.q, limit: 8, sort: "featured" } })
        .then((r) => {
          const withImg = (r.data.products || []).find((p) => p.image);
          if (withImg) setCatImgs((prev) => ({ ...prev, [c.key]: withImg.image }));
        }).catch(() => {});
    });
  }, []);

  useEffect(() => {
    const id = setInterval(() => setSlide((s) => (s + 1) % SLIDES.length), 5000);
    return () => clearInterval(id);
  }, []);

  const Feature = ({ icon: Icon, title, text }) => (
    <div className="flex items-start gap-4 p-6 border border-gray-200 bg-white">
      <Icon className="text-brand-red shrink-0" size={26} />
      <div><h4 className="font-semibold text-gray-900">{title}</h4><p className="text-sm text-gray-500 mt-1">{text}</p></div>
    </div>
  );

  return (
    <div>
      <LocationPopup />
      {/* HERO */}
      <section className="relative bg-[#0a0a0a] text-white overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent" />
        <div className="relative mx-auto max-w-7xl px-4 py-28 md:py-40">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <span className="inline-block text-xs uppercase tracking-[0.3em] text-brand-yellow mb-6 border border-brand-yellow/40 px-3 py-1">{t("home.badge")}</span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[0.95] tracking-tight">
              {t("home.title1")}<br /><span className="text-white/50">{t("home.title2")}</span>
            </h1>
            <p className="mt-8 text-lg text-gray-300 max-w-xl leading-relaxed">{t("home.subtitle")}</p>
          </motion.div>
        </div>
      </section>

      {/* SLIDESHOW WIDGET */}
      <section className="mx-auto max-w-7xl px-4 pt-16" data-testid="home-slideshow">
        <div className="relative overflow-hidden border border-gray-200 bg-black">
          <AnimatePresence mode="wait">
            <motion.img
              key={slide}
              src={SLIDES[slide]}
              alt={t("home.slideTitle")}
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.7 }}
              className="w-full h-[280px] sm:h-[420px] object-cover" />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <div className="absolute bottom-0 left-0 p-6 sm:p-10 max-w-xl text-white">
            <h2 className="font-heading text-2xl sm:text-4xl font-extrabold">{t("home.slideTitle")}</h2>
            <p className="mt-2 text-white/85 text-sm sm:text-base">{t("home.slideSub")}</p>
            <Link to="/boutique?brand=Vesta" className="mt-5 inline-flex items-center gap-2 bg-brand-red text-white px-6 py-3 text-sm font-bold hover:bg-[#b81c14] transition-colors" data-testid="slideshow-cta">
              {t("home.slideBtn")} <ArrowRight size={16} />
            </Link>
          </div>
          <button onClick={() => setSlide((s) => (s - 1 + SLIDES.length) % SLIDES.length)} className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-black p-2 rounded-full" data-testid="slideshow-prev" aria-label="Précédent"><ChevronLeft size={20} /></button>
          <button onClick={() => setSlide((s) => (s + 1) % SLIDES.length)} className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-black p-2 rounded-full" data-testid="slideshow-next" aria-label="Suivant"><ChevronRight size={20} /></button>
          <div className="absolute bottom-4 right-4 flex gap-2">
            {SLIDES.map((_, i) => (
              <button key={i} onClick={() => setSlide(i)} className={`h-2 rounded-full transition-all ${i === slide ? "w-6 bg-white" : "w-2 bg-white/50"}`} data-testid={`slideshow-dot-${i}`} aria-label={`Slide ${i + 1}`} />
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES BENTO */}
      <section className="mx-auto max-w-7xl px-4 py-20">
        <div className="flex items-end justify-between mb-10">
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">{t("home.categories")}</h2>
          <Link to="/boutique" className="hidden sm:flex items-center gap-2 text-brand-red font-semibold hover:gap-3 transition-all">{t("home.viewAll")} <ArrowRight size={18} /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[200px] gap-4">
          {CATS.map((c) => (
            <Link key={c.key} to={c.to} className="group relative overflow-hidden border border-gray-200 bg-gray-100" data-testid={`home-cat-${c.key}`}>
              <img src={catImgs[c.key] || c.fallback} alt={t(c.label)} className="h-full w-full object-contain p-4 md:p-6 transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 p-5">
                <h3 className="text-white font-heading text-lg md:text-xl font-bold">{t(c.label)}</h3>
                <span className="text-white/80 text-sm flex items-center gap-1 mt-1">{t("home.explore")} <ArrowRight size={14} /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 py-10">
          <h2 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight mb-10">{t("home.featured")}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}

      {/* FRIPERIE LYLY */}
      <section className="mx-auto max-w-7xl px-4 py-10">
        <a href={STORE.friperie} target="_blank" rel="noopener noreferrer" className="group flex flex-col sm:flex-row items-center justify-between gap-6 bg-brand-navy text-white p-8 md:p-10 hover:bg-black transition-colors" data-testid="home-friperie">
          <div className="flex items-center gap-5">
            <Store size={40} className="text-brand-yellow shrink-0" />
            <div>
              <h3 className="font-heading text-2xl md:text-3xl font-extrabold">{t("home.friperieTitle")}</h3>
              <p className="text-white/80 text-sm mt-1 max-w-xl">{t("home.friperieSub")}</p>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 bg-brand-yellow text-black px-6 py-3 font-bold shrink-0 group-hover:gap-3 transition-all">{t("home.friperieBtn")} <ArrowRight size={16} /></span>
        </a>
      </section>

      {/* FEATURES */}
      <section className="mx-auto max-w-7xl px-4 py-14">
        <div className="grid gap-4 md:grid-cols-4">
          <Feature icon={ShieldCheck} title={t("home.f1t")} text={t("home.f1d")} />
          <Feature icon={Truck} title={t("home.f2t")} text={t("home.f2d")} />
          <Feature icon={CreditCard} title={t("home.f3t")} text={t("home.f3d")} />
          <Feature icon={Headphones} title={t("home.f4t")} text={t("home.f4d")} />
        </div>
      </section>

      {/* BIG BOTTOM CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-24">
        <div className="relative bg-brand-red text-white p-12 md:p-24 overflow-hidden">
          <div className="absolute -right-10 -top-10 h-56 w-56 rounded-full bg-brand-yellow/20" />
          <div className="relative max-w-2xl">
            <h2 className="font-heading text-4xl md:text-6xl font-extrabold tracking-tight leading-none">{t("home.ctaTitle")}</h2>
            <p className="mt-6 text-white/90 text-lg leading-relaxed">{t("home.ctaSub")}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/boutique" className="group flex items-center gap-2 bg-white text-brand-red px-10 py-5 text-lg font-bold hover:bg-brand-yellow hover:text-black transition-colors" data-testid="home-cta-shop">
                {t("home.ctaBtn")} <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/contact" className="border-2 border-white px-10 py-5 text-lg font-bold hover:bg-white/10 transition-colors" data-testid="home-cta-contact">{t("home.ctaContact")}</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FLOATING CTA */}
      <Link to="/boutique" className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-brand-red text-white px-6 py-4 font-bold shadow-2xl hover:bg-[#b81c14] transition-colors rounded-full" data-testid="floating-cta">
        <ShoppingBag size={20} /> {t("home.floating")}
      </Link>
    </div>
  );
}
